import { useEffect, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { BarElement, CategoryScale, Chart as ChartJS, LinearScale, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  addCreditCard,
  addEntry,
  addInstallment,
  addRecurring,
  clearSession,
  deleteCreditCard,
  deleteEntry,
  deleteExpense,
  getAuthToken,
  getCreditCards,
  getFinishedInstallments,
  getMe,
  getLatestMonthlyClosing,
  getMonthlyForecast,
  getExpenses,
  getEntries,
  loginWithGoogle,
  openMonthlyClosing,
  reopenMonthlyClosing,
  saveMonthlyClosingDraft,
  setExpenseStatus,
  toggleCreditCard,
  toggleExpense,
  typeClass,
  typeLabel,
  updateCreditCard,
  updateExpense,
  finalizeMonthlyClosing
} from './api';
import {
  CreditCardDto,
  EditExpenseModel,
  ExpenseDto,
  ExpenseType,
  EntryDto,
  MonthlyClosingDto,
  MonthlyClosingSummaryDto,
  MonthlyForecastDto,
  NewCreditCard,
  NewEntry,
  NewInstallmentExpense,
  NewRecurringExpense,
  UserDto
} from './types';
import { format, addMonths, differenceInCalendarMonths, isWithinInterval } from 'date-fns';
import { ptBR } from 'date-fns/locale';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

function currency(value: number) {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function parseCurrencyInput(value: string) {
  const digits = value.replace(/\D/g, '');
  return digits ? Number(digits) / 100 : 0;
}

function outflowCurrency(value: number) {
  return `− ${currency(Math.abs(value))}`;
}

function parseDate(value?: string | null) {
  return value ? new Date(value) : null;
}

function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1, 12, 0, 0);
}

function forecastMonthLabel(year: number, month: number) {
  const label = new Date(year, month - 1, 1, 12, 0, 0).toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric'
  });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: { client_id: string; callback: (response: { credential?: string }) => void }) => void;
          renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
        };
      };
    };
  }
}

function loadGoogleScript() {
  return new Promise<void>((resolve, reject) => {
    if (window.google?.accounts?.id) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Não foi possível carregar o script do Google.'));
    document.head.appendChild(script);
  });
}

interface ParcelaFinal {
  expense: ExpenseDto;
  valor: number;
}

export default function App() {
  const [expenses, setExpenses] = useState<ExpenseDto[]>([]);
  const [entries, setEntries] = useState<EntryDto[]>([]);
  const [entryHistory, setEntryHistory] = useState<EntryDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [filterText, setFilterText] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('active');
  const [showVencendo, setShowVencendo] = useState(false);
  const [editing, setEditing] = useState<EditExpenseModel | null>(null);
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [showEntryHistoryModal, setShowEntryHistoryModal] = useState(false);
  const [categoryModal, setCategoryModal] = useState<ExpenseType | null>(null);
  const [catSearch, setCatSearch] = useState('');
  const [catStatus, setCatStatus] = useState('all');
  const [catCard, setCatCard] = useState('');
  const [showCatCreate, setShowCatCreate] = useState(false);
  const [catEditing, setCatEditing] = useState<EditExpenseModel | null>(null);
  const [closingMode, setClosingMode] = useState<'preview' | 'closing'>('preview');
  const [adjustmentsDirty, setAdjustmentsDirty] = useState(false);
  const [showClosingModal, setShowClosingModal] = useState(false);
  const [closing, setClosing] = useState<MonthlyClosingDto | null>(null);
  const [savedForecast, setSavedForecast] = useState<MonthlyForecastDto | null>(null);
  const [latestClosing, setLatestClosing] = useState<MonthlyClosingSummaryDto | null>(null);
  const [authUser, setAuthUser] = useState<UserDto | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [creditCards, setCreditCards] = useState<CreditCardDto[]>([]);
  const [showCreditCardsModal, setShowCreditCardsModal] = useState(false);
  const [editingCreditCard, setEditingCreditCard] = useState<CreditCardDto | null>(null);
  const [newCreditCard, setNewCreditCard] = useState<NewCreditCard>({
    name: '',
    brand: '',
    lastFourDigits: '',
    limit: '',
    closingDay: '',
    dueDay: '',
    isActive: true
  });

  const [novaParcela, setNovaParcela] = useState<NewInstallmentExpense>({
    name: '',
    amount: '',
    installments: '',
    originalName: '',
    purchaseDate: '',
    dueDate: '',
    autoPayment: false,
    includeInForecast: true,
    creditCardId: ''
  });

  const [novaRecorrenciaCartao, setNovaRecorrenciaCartao] = useState<NewRecurringExpense>({
    name: '',
    amount: '',
    onCard: true,
    originalName: '',
    dueDate: '',
    dueDay: '',
    autoPayment: false,
    includeInForecast: true,
    creditCardId: ''
  });

  const [novaRecorrenciaFora, setNovaRecorrenciaFora] = useState<NewRecurringExpense>({
    name: '',
    amount: '',
    onCard: false,
    originalName: '',
    dueDate: '',
    dueDay: '',
    autoPayment: false,
    includeInForecast: true
  });

  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();
  const currentMonthName = format(now, 'MMMM', { locale: ptBR });

  const [newEntry, setNewEntry] = useState<NewEntry>({
    name: '',
    amount: '',
    month: currentMonth,
    year: currentYear,
    isSalary: true
  });
  const [historyMonth, setHistoryMonth] = useState(currentMonth);
  const [historyYear, setHistoryYear] = useState(currentYear);
  const [closingMonth, setClosingMonth] = useState(currentMonth);
  const [closingYear, setClosingYear] = useState(currentYear);
  const [commitmentAmounts, setCommitmentAmounts] = useState<Record<string, number>>({});
  const [commitmentInclusions, setCommitmentInclusions] = useState<Record<string, boolean>>({});
  const [commitmentFutureUpdates, setCommitmentFutureUpdates] = useState<Record<string, boolean>>({});
  const [commitmentPaidAnotherWay, setCommitmentPaidAnotherWay] = useState<Record<string, boolean>>({});
  const [commitmentPaymentNotes, setCommitmentPaymentNotes] = useState<Record<string, string>>({});
  const [cardAmounts, setCardAmounts] = useState<Record<string, number>>({});
  const [cardInclusions, setCardInclusions] = useState<Record<string, boolean>>({});
  const [cardPaid, setCardPaid] = useState<Record<string, boolean>>({});
  const [cardPaymentNotes, setCardPaymentNotes] = useState<Record<string, string>>({});
  const [newAdjustment, setNewAdjustment] = useState({ description: '', amount: '' as number | '' });

  async function carregar() {
    setLoading(true);
    try {
      const today = new Date();
      try {
        const finished = await getFinishedInstallments(today.getFullYear(), today.getMonth() + 1);
        if (finished.length > 0) {
          await Promise.all(finished.map(id => setExpenseStatus(id, false)));
        }
      } catch (err) {
        console.warn('Falha ao fechar parcelamentos finalizados.', err);
      }
      const [exp, inc, closingSummary] = await Promise.all([
        getExpenses(today.getFullYear(), today.getMonth() + 1),
        getEntries(today.getFullYear(), today.getMonth() + 1),
        getLatestMonthlyClosing()
      ]);
      setExpenses(exp ?? []);
      setEntries(inc ?? []);
      setLatestClosing(closingSummary);
      try {
        setCreditCards(await getCreditCards() ?? []);
      } catch (err) {
        console.warn('Falha ao buscar cartões de crédito.', err);
      }
      setSavedForecast(await getMonthlyForecast(currentYear, currentMonth));
    } catch (err) {
      console.error(err);
      setMessage('Não consegui buscar os dados agora.');
    } finally {
      setLoading(false);
    }
  }

  async function refreshLatestForecast() {
    try {
      setLatestClosing(await getLatestMonthlyClosing());
      setSavedForecast(await getMonthlyForecast(currentYear, currentMonth));
    } catch (err) {
      console.warn('Falha ao atualizar a previsão de fechamento.', err);
    }
  }

  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;

  useEffect(() => {
    let cancelled = false;

    async function bootstrapSession() {
      const token = getAuthToken();
      if (!token) {
        setAuthLoading(false);
        return;
      }

      try {
        const me = await getMe();
        if (!cancelled) {
          setAuthUser(me);
        }
      } catch {
        clearSession();
      } finally {
        if (!cancelled) {
          setAuthLoading(false);
        }
      }
    }

    bootstrapSession();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!authUser) return;
    carregar();
  }, [authUser]);

  useEffect(() => {
    if (authLoading || authUser) return;
    if (!googleClientId) {
      setMessage('Defina VITE_GOOGLE_CLIENT_ID no frontend para habilitar login.');
      return;
    }

    let cancelled = false;

    async function initGoogleButton() {
      try {
        await loadGoogleScript();
        if (cancelled || !window.google?.accounts?.id) return;

        const target = document.getElementById('google-signin-btn');
        if (!target) return;
        target.innerHTML = '';

        window.google.accounts.id.initialize({
          client_id: googleClientId,
          callback: async ({ credential }) => {
            if (!credential) return;
            setLoading(true);
            setMessage(null);
            try {
              const auth = await loginWithGoogle(credential);
              setAuthUser(auth.user);
              setMessage('Login realizado com sucesso.');
            } catch (err: any) {
              setMessage(err.message || 'Falha ao autenticar com Google.');
            } finally {
              setLoading(false);
            }
          }
        });

        window.google.accounts.id.renderButton(target, {
          type: 'standard',
          theme: 'outline',
          shape: 'pill',
          size: 'large',
          text: 'signin_with',
          width: 280
        });
      } catch {
        setMessage('Não consegui carregar o login do Google.');
      }
    }

    initGoogleButton();
    return () => {
      cancelled = true;
    };
  }, [authLoading, authUser, googleClientId]);

  const filteredExpenses = useMemo(() => {
    return expenses.filter(e => {
      const matchText = !filterText || e.name.toLowerCase().includes(filterText.toLowerCase());
      const matchType = !filterType || e.type.toString() === filterType;
      const matchStatus =
        filterStatus === 'all' ||
        (filterStatus === 'active' && e.isActive) ||
        (filterStatus === 'inactive' && !e.isActive);
      return matchText && matchType && matchStatus;
    });
  }, [expenses, filterText, filterType, filterStatus]);

  const creditCardMap = useMemo(
    () => Object.fromEntries(creditCards.map(c => [c.id, c])),
    [creditCards]
  );

  function creditCardLabel(id?: string | null) {
    if (!id) return null;
    const card = creditCardMap[id];
    if (!card) return null;
    return `${card.name}${card.lastFourDigits ? ` · final ${card.lastFourDigits}` : ''}`;
  }

  const categoryConfig = useMemo(() => ({
    [ExpenseType.Installment]: {
      eyebrow: 'Categoria',
      title: 'Parcelamento/Financiamento',
      description: 'Compras parceladas e financiamentos, vinculados ou não a um cartão.',
      icon: 'receipt_long',
      addLabel: 'Novo parcelamento/financiamento'
    },
    [ExpenseType.RecurringCard]: {
      eyebrow: 'Categoria',
      title: 'Assinatura no cartão',
      description: 'Assinaturas e cobranças recorrentes no cartão de crédito.',
      icon: 'credit_card',
      addLabel: 'Nova assinatura'
    },
    [ExpenseType.RecurringOffCard]: {
      eyebrow: 'Categoria',
      title: 'Pagamentos manuais',
      description: 'Pagamentos manuais do mês, pagos fora do cartão ou por outros meios.',
      icon: 'event_repeat',
      addLabel: 'Novo pagamento manual'
    }
  }), []);

  const categoryBase = useMemo(() => {
    if (!categoryModal) return [];
    return expenses.filter(e => e.type === categoryModal);
  }, [expenses, categoryModal]);

  const categoryExpenses = useMemo(() => {
    return categoryBase
      .filter(e => {
        const matchText = !catSearch || e.name.toLowerCase().includes(catSearch.toLowerCase());
        const matchStatus =
          catStatus === 'all' ||
          (catStatus === 'active' && e.isActive) ||
          (catStatus === 'inactive' && !e.isActive);
        const matchCard = !catCard ||
          (catCard === 'none' ? !e.creditCardId : e.creditCardId === catCard);
        return matchText && matchStatus && matchCard;
      })
      .slice()
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [categoryBase, catSearch, catStatus, catCard]);

  const categoryCardOptions = useMemo(() => {
    const counts = new Map<string, number>();
    let withoutCard = 0;
    for (const expense of categoryBase) {
      if (expense.creditCardId) counts.set(expense.creditCardId, (counts.get(expense.creditCardId) ?? 0) + 1);
      else withoutCard += 1;
    }
    const cards = creditCards
      .slice()
      .sort((a, b) => Number(b.isActive) - Number(a.isActive) || a.name.localeCompare(b.name))
      .map(c => ({
        id: c.id,
        label: `${c.name}${c.lastFourDigits ? ` · final ${c.lastFourDigits}` : ''}${c.isActive ? '' : ' (inativo)'} (${counts.get(c.id) ?? 0})`
      }));
    return { cards, withoutCard };
  }, [categoryBase, creditCards]);

  const categoryTotal = useMemo(
    () => categoryExpenses.reduce((total, expense) => total + expense.amount, 0),
    [categoryExpenses]
  );

  const categoryCounts = useMemo(() => ({
    total: categoryBase.length,
    active: categoryBase.filter(e => e.isActive).length,
    inactive: categoryBase.filter(e => !e.isActive).length
  }), [categoryBase]);

  function openCategory(type: ExpenseType) {
    setCatSearch('');
    setCatStatus('all');
    setCatCard('');
    setShowCatCreate(false);
    setCatEditing(null);
    setCategoryModal(type);
  }

  function handleCategoryEdit(exp: ExpenseDto) {
    setCatEditing({
      id: exp.id,
      name: exp.name,
      originalName: exp.originalName,
      amount: exp.amount,
      type: exp.type,
      installments: exp.type === ExpenseType.Installment ? (exp.installments ?? 1) : null,
      purchaseDate: exp.purchaseDate?.substring(0, 10) ?? null,
      dueDate: exp.dueDate?.substring(0, 10) ?? null,
      dueDay: exp.dueDay ?? null,
      autoPayment: exp.autoPayment ?? false,
      includeInForecast: exp.includeInForecast ?? true,
      isActive: exp.isActive,
      creditCardId: exp.creditCardId ?? null
    });
    setShowCatCreate(false);
  }

  async function handleCatEditSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!catEditing || !categoryModal) return;
    setLoading(true);
    resetMessages();
    try {
      const payload: EditExpenseModel = {
        ...catEditing,
        type: categoryModal,
        amount: Number(catEditing.amount),
        installments: categoryModal === ExpenseType.Installment ? (catEditing.installments ?? 1) : null
      };
      const updated = await updateExpense(payload);
      setExpenses(prev => prev.map(x => (x.id === updated.id ? updated : x)));
      await refreshLatestForecast();
      setCatEditing(null);
      setMessage('Despesa atualizada.');
    } catch (err: any) {
      setMessage(err.message || 'Não deu para salvar as alterações.');
    } finally {
      setLoading(false);
    }
  }

  const totalFilteredExpenses = useMemo(
    () => filteredExpenses.reduce((total, expense) => total + expense.amount, 0),
    [filteredExpenses]
  );

  const parcelasUltimoMes: ParcelaFinal[] = useMemo(() => {
    const hoje = new Date();
    return expenses
      .filter(e => e.isActive && e.type === ExpenseType.Installment && e.installments)
      .flatMap(e => {
        // Sem data de compra, usa o cadastramento como base (igual à lista principal).
        const base = parseDate(e.purchaseDate ?? '') ?? parseDate(e.createdAt);
        if (!base || !e.installments) return [];
        const monthsPassed = differenceInCalendarMonths(startOfMonth(hoje), startOfMonth(base)) + 1;
        if (monthsPassed === e.installments) {
          return [{ expense: e, valor: e.amount }];
        }
        return [];
      });
  }, [expenses]);

  const totalUltimoMesParcelas = useMemo(() => parcelasUltimoMes.reduce((acc, p) => acc + p.valor, 0), [parcelasUltimoMes]);

  const contasVencendoHoje = useMemo(() => {
    const hoje = new Date();
    const day = hoje.getDate();
    const isToday = (iso?: string | null) => {
      if (!iso) return false;
      const d = parseDate(iso);
      return !!d && d.getFullYear() === hoje.getFullYear() && d.getMonth() === hoje.getMonth() && d.getDate() === day;
    };
    const manuais = expenses.filter(e =>
      e.isActive && e.type === ExpenseType.RecurringOffCard && (e.dueDay === day || isToday(e.dueDate)));
    const bases: Record<string, number> = {};
    for (const e of expenses) {
      if (!e.isActive || !e.creditCardId) continue;
      if (e.type !== ExpenseType.Installment && e.type !== ExpenseType.RecurringCard) continue;
      bases[e.creditCardId] = (bases[e.creditCardId] ?? 0) + e.amount;
    }
    const cartoes = creditCards
      .filter(c => c.isActive && c.dueDay === day)
      .map(c => ({ card: c, fatura: bases[c.id] ?? 0 }));
    return { manuais, cartoes };
  }, [expenses, creditCards]);

  const totalVencendoHoje = useMemo(
    () => contasVencendoHoje.manuais.reduce((acc, e) => acc + e.amount, 0)
      + contasVencendoHoje.cartoes.reduce((acc, c) => acc + c.fatura, 0),
    [contasVencendoHoje]
  );

  const totalParcelamentos = useMemo(
    () => expenses.filter(e => e.isActive && e.type === ExpenseType.Installment).reduce((acc, e) => acc + e.amount, 0),
    [expenses]
  );
  const totalRecorrenciaCartao = useMemo(
    () => expenses.filter(e => e.isActive && e.type === ExpenseType.RecurringCard).reduce((acc, e) => acc + e.amount, 0),
    [expenses]
  );
  const totalRecorrenciaForaCartao = useMemo(
    () => expenses.filter(e => e.isActive && e.type === ExpenseType.RecurringOffCard).reduce((acc, e) => acc + e.amount, 0),
    [expenses]
  );
  const totalGrafico = totalParcelamentos + totalRecorrenciaCartao;
  const totalEntries = useMemo(
    () => entries.reduce((acc, entry) => acc + entry.amount, 0),
    [entries]
  );
  const hasCurrentMonthEntries = entries.length > 0;
  const entryCardValue = hasCurrentMonthEntries
    ? totalEntries
    : latestClosing?.forecastIncome;
  const closingSelectedCommitmentsTotal = useMemo(() => {
    if (!closing || closing.isFinalized) return closing?.totalCommitments ?? 0;

    return closing.commitments.reduce((total, commitment) => {
      const expenseId = commitment.expenseId;
      const isIncluded = expenseId
        ? (commitmentInclusions[expenseId] ?? commitment.isIncluded)
        : commitment.isIncluded;
      if (!isIncluded) return total;

      const amount = expenseId
        ? (commitmentAmounts[expenseId] ?? commitment.amount)
        : commitment.amount;
      return total + amount;
    }, 0);
  }, [closing, commitmentAmounts, commitmentInclusions]);

  const closingSelectedCardsTotal = useMemo(() => {
    if (!closing || closing.isFinalized) return closing?.totalCards ?? 0;

    return (closing.cards ?? []).reduce((total, card) => {
      const cardId = card.creditCardId;
      const isIncluded = cardId
        ? (cardInclusions[cardId] ?? card.isIncluded)
        : card.isIncluded;
      if (!isIncluded) return total;

      const amount = cardId
        ? (cardAmounts[cardId] ?? card.amount)
        : card.amount;
      return total + amount;
    }, 0);
  }, [closing, cardAmounts, cardInclusions]);

  const closingResidualPreview = useMemo(() => {
    if (!closing || closing.isFinalized) return closing?.residualBalance ?? 0;

    return closing.previousResidual
      + closing.totalEntries
      - closingSelectedCommitmentsTotal
      - closingSelectedCardsTotal
      - closing.totalAdjustments;
  }, [closing, closingSelectedCommitmentsTotal, closingSelectedCardsTotal]);
  const hasUnsavedCommitmentChanges = useMemo(() => {
    if (!closing || closing.isFinalized) return false;

    const commitmentChanged = closing.commitments.some(commitment => {
      const expenseId = commitment.expenseId;
      if (!expenseId) return false;

      const isIncluded = commitmentInclusions[expenseId] ?? commitment.isIncluded;
      const paidAnotherWay = isIncluded
        ? false
        : (commitmentPaidAnotherWay[expenseId] ?? commitment.paidAnotherWay);

      return (commitmentAmounts[expenseId] ?? commitment.amount) !== commitment.amount
        || isIncluded !== commitment.isIncluded
        || (commitmentFutureUpdates[expenseId] ?? false)
        || paidAnotherWay !== commitment.paidAnotherWay
        || (commitmentPaymentNotes[expenseId] ?? commitment.paymentMethodNote ?? '').trim() !== (commitment.paymentMethodNote ?? '').trim();
    });

    const cardChanged = (closing.cards ?? []).some(card => {
      const cardId = card.creditCardId;
      if (!cardId) return false;

      return (cardAmounts[cardId] ?? card.amount) !== card.amount
        || (cardInclusions[cardId] ?? card.isIncluded) !== card.isIncluded
        || (cardPaid[cardId] ?? card.isPaid) !== card.isPaid
        || (cardPaymentNotes[cardId] ?? card.paymentMethodNote ?? '').trim() !== (card.paymentMethodNote ?? '').trim();
    });

    return commitmentChanged || cardChanged;
  }, [closing, commitmentAmounts, commitmentFutureUpdates, commitmentInclusions, commitmentPaidAnotherWay, commitmentPaymentNotes, cardAmounts, cardInclusions, cardPaid, cardPaymentNotes]);
  const totalDespesasAtivas = expenses.filter(e => e.isActive).length;
  const chartItems = useMemo(() => {
    return [
      { label: 'Parcelamentos/Financiamentos', value: totalParcelamentos, color: '#4f8bff' },
      { label: 'Recorrência cartão', value: totalRecorrenciaCartao, color: '#3ac26b' }
    ].sort((a, b) => a.value - b.value);
  }, [totalParcelamentos, totalRecorrenciaCartao]);

  const chartData = useMemo(() => {
    return {
      labels: chartItems.map(item => item.label),
      datasets: [
        {
          label: 'Total',
          data: chartItems.map(item => item.value),
          backgroundColor: chartItems.map(item => item.color),
          borderRadius: 8
        }
      ]
    };
  }, [chartItems]);

  const chartOptions = useMemo(() => {
    const max = Math.max(totalParcelamentos, totalRecorrenciaCartao, 1);
    return {
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 24 } },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx: any) => outflowCurrency(Number(ctx.parsed.y ?? 0))
          }
        },
        datalabels: {
          color: '#c93434',
          anchor: 'end',
          align: 'end',
          offset: 8,
          clamp: true,
          clip: false,
          font: { weight: '700' },
          formatter: (value: number) => outflowCurrency(Number(value))
        }
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: '#7b8ba2' }
        },
        y: {
          beginAtZero: true,
          ticks: { color: '#7b8ba2', padding: 8 },
          suggestedMax: max * 1.2
        }
      }
    };
  }, [totalParcelamentos, totalRecorrenciaCartao]);

  const despesasVencendo = useMemo(() => {
    const hoje = new Date();
    const limite = addMonths(hoje, 0);
    return expenses
      .filter(e => e.isActive && e.type === ExpenseType.RecurringOffCard && e.dueDate)
      .filter(e => {
        const due = parseDate(e.dueDate!);
        if (!due) return false;
        const intervalo = isWithinInterval(due, {
          start: new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate()),
          end: new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() + 7)
        });
        return intervalo;
      })
      .sort((a, b) => (parseDate(a.dueDate!)?.getTime() ?? 0) - (parseDate(b.dueDate!)?.getTime() ?? 0));
  }, [expenses]);

  function resetMessages() {
    setMessage(null);
  }

  async function handleParcelaSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!novaParcela.name || !novaParcela.amount || !novaParcela.installments) return;
    setLoading(true);
    resetMessages();
    try {
      const purchaseDate = novaParcela.purchaseDate ? novaParcela.purchaseDate : null;
      const created = await addInstallment({
        ...novaParcela,
        amount: Number(novaParcela.amount),
        installments: Number(novaParcela.installments),
        purchaseDate
      });
      setExpenses(prev => [created, ...prev]);
      await refreshLatestForecast();
      setNovaParcela({ name: '', amount: '', installments: '', originalName: '', purchaseDate: '', dueDate: '', autoPayment: false, includeInForecast: true, creditCardId: '' });
      setShowCatCreate(false);
      setMessage('Parcelamento/financiamento adicionado.');
    } catch (err: any) {
      setMessage(err.message || 'Não deu para salvar.');
    } finally {
      setLoading(false);
    }
  }

  async function handleRecorrenciaCartaoSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!novaRecorrenciaCartao.name || !novaRecorrenciaCartao.amount) return;
    setLoading(true);
    resetMessages();
    try {
      const created = await addRecurring({
        ...novaRecorrenciaCartao,
        amount: Number(novaRecorrenciaCartao.amount),
        dueDay: novaRecorrenciaCartao.dueDay ? Number(novaRecorrenciaCartao.dueDay) : undefined,
        dueDate: novaRecorrenciaCartao.dueDate ? novaRecorrenciaCartao.dueDate : undefined
      });
      setExpenses(prev => [created, ...prev]);
      await refreshLatestForecast();
      setNovaRecorrenciaCartao({ name: '', amount: '', onCard: true, originalName: '', dueDate: '', dueDay: '', autoPayment: false, includeInForecast: true, creditCardId: '' });
      setShowCatCreate(false);
      setMessage('Recorrente no cartão adicionada.');
    } catch (err: any) {
      setMessage(err.message || 'Não deu para salvar.');
    } finally {
      setLoading(false);
    }
  }

  async function handleRecorrenciaForaSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!novaRecorrenciaFora.name || !novaRecorrenciaFora.amount) return;
    setLoading(true);
    resetMessages();
    try {
      const created = await addRecurring({
        ...novaRecorrenciaFora,
        amount: Number(novaRecorrenciaFora.amount),
        dueDay: novaRecorrenciaFora.dueDay ? Number(novaRecorrenciaFora.dueDay) : undefined,
        dueDate: novaRecorrenciaFora.dueDate ? novaRecorrenciaFora.dueDate : undefined
      });
      setExpenses(prev => [created, ...prev]);
      await refreshLatestForecast();
      setNovaRecorrenciaFora({ name: '', amount: '', onCard: false, originalName: '', dueDate: '', dueDay: '', autoPayment: false, includeInForecast: true });
      setShowCatCreate(false);
      setMessage('Pagamento manual adicionado.');
    } catch (err: any) {
      setMessage(err.message || 'Não deu para salvar.');
    } finally {
      setLoading(false);
    }
  }

  async function handleEntrySubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!newEntry.name || !newEntry.amount) return;
    setLoading(true);
    resetMessages();
    try {
      const created = await addEntry({ ...newEntry, amount: Number(newEntry.amount) });
      if (created.month === currentMonth && created.year === currentYear) {
        setEntries(previous => [created, ...previous]);
      }
      if (showEntryHistoryModal && created.month === historyMonth && created.year === historyYear) {
        setEntryHistory(previous => [created, ...previous]);
      }
      await refreshLatestForecast();
      setNewEntry({ name: '', amount: '', month: currentMonth, year: currentYear, isSalary: true });
      setShowEntryModal(false);
      setMessage('Entrada adicionada.');
    } catch (err: any) {
      setMessage(err.message || 'Não deu para salvar a entrada.');
    } finally {
      setLoading(false);
    }
  }

  async function loadEntryHistory(year: number, month: number) {
    setLoading(true);
    resetMessages();
    try {
      setEntryHistory(await getEntries(year, month));
    } catch (err: any) {
      setMessage(err.message || 'Não deu para buscar o histórico de entradas.');
    } finally {
      setLoading(false);
    }
  }

  function openEntryHistory() {
    setHistoryMonth(currentMonth);
    setHistoryYear(currentYear);
    setEntryHistory(entries);
    setShowEntryHistoryModal(true);
    void loadEntryHistory(currentYear, currentMonth);
  }

  function applyClosing(nextClosing: MonthlyClosingDto, startUnchecked = false) {
    setClosing(nextClosing);
    setAdjustmentsDirty(false);
    setCommitmentAmounts(Object.fromEntries(
      nextClosing.commitments
        .filter(item => item.expenseId)
        .map(item => [item.expenseId as string, item.amount])
    ));
    // Na prévia, o checklist começa zerado para marcar conforme paga.
    setCommitmentInclusions(Object.fromEntries(
      nextClosing.commitments
        .filter(item => item.expenseId)
        .map(item => [item.expenseId as string, startUnchecked ? false : item.isIncluded])
    ));
    setCommitmentFutureUpdates(Object.fromEntries(
      nextClosing.commitments
        .filter(item => item.expenseId)
        .map(item => [item.expenseId as string, false])
    ));
    setCommitmentPaidAnotherWay(Object.fromEntries(
      nextClosing.commitments
        .filter(item => item.expenseId)
        .map(item => [item.expenseId as string, item.paidAnotherWay])
    ));
    setCommitmentPaymentNotes(Object.fromEntries(
      nextClosing.commitments
        .filter(item => item.expenseId)
        .map(item => [item.expenseId as string, item.paymentMethodNote ?? ''])
    ));
    setCardAmounts(Object.fromEntries(
      (nextClosing.cards ?? [])
        .filter(item => item.creditCardId)
        .map(item => [item.creditCardId as string, item.amount])
    ));
    setCardInclusions(Object.fromEntries(
      (nextClosing.cards ?? [])
        .filter(item => item.creditCardId)
        .map(item => [item.creditCardId as string, startUnchecked ? false : item.isIncluded])
    ));
    setCardPaid(Object.fromEntries(
      (nextClosing.cards ?? [])
        .filter(item => item.creditCardId)
        .map(item => [item.creditCardId as string, item.isPaid])
    ));
    setCardPaymentNotes(Object.fromEntries(
      (nextClosing.cards ?? [])
        .filter(item => item.creditCardId)
        .map(item => [item.creditCardId as string, item.paymentMethodNote ?? ''])
    ));
  }

  async function handleOpenClosing(mode: 'preview' | 'closing' = 'preview') {
    setClosingMode(mode);
    // O fechamento herda a prévia: se o mesmo período já está carregado
    // (ex.: checklist marcado na prévia, salvo ou não), reaproveita o estado.
    if (mode === 'closing' && closing && closing.year === closingYear && closing.month === closingMonth) {
      setShowClosingModal(true);
      return;
    }
    setLoading(true);
    resetMessages();
    try {
      const nextClosing = await openMonthlyClosing(closingYear, closingMonth);
      // Prévia de mês intocado começa zerada; de mês já trabalhado, retoma o salvo.
      applyClosing(nextClosing, mode === 'preview' && !nextClosing.userSavedDraft);
      setShowClosingModal(true);
    } catch (err: any) {
      setMessage(err.message || 'Não foi possível abrir o fechamento mensal.');
    } finally {
      setLoading(false);
    }
  }

  async function handleChangeClosingPeriod(year: number, month: number) {
    setLoading(true);
    resetMessages();
    try {
      const nextClosing = await openMonthlyClosing(year, month);
      applyClosing(nextClosing, closingMode === 'preview' && !nextClosing.userSavedDraft);
    } catch (err: any) {
      setMessage(err.message || 'Não foi possível carregar o fechamento.');
    } finally {
      setLoading(false);
    }
  }

  function handleAddAdjustment(event: React.FormEvent) {
    event.preventDefault();
    if (!closing || !newAdjustment.description.trim() || Number(newAdjustment.amount) <= 0) return;
    const adjustment = {
      id: crypto.randomUUID(),
      description: newAdjustment.description.trim(),
      amount: Number(newAdjustment.amount),
      createdAt: new Date().toISOString()
    };
    setClosing({ ...closing, adjustments: [...closing.adjustments, adjustment], totalAdjustments: closing.totalAdjustments + adjustment.amount });
    setAdjustmentsDirty(true);
    setNewAdjustment({ description: '', amount: '' });
  }

  function handleDeleteAdjustment(id: string) {
    if (!closing) return;
    const adjustments = closing.adjustments.filter(item => item.id !== id);
    setClosing({ ...closing, adjustments, totalAdjustments: adjustments.reduce((sum, item) => sum + item.amount, 0) });
    setAdjustmentsDirty(true);
  }

  async function handleSaveClosingDraft() {
    if (!closing || closing.isFinalized) return;

    const commitments = closing.commitments.flatMap(commitment => {
      const expenseId = commitment.expenseId;
      if (!expenseId) return [];

      const isIncluded = commitmentInclusions[expenseId] ?? commitment.isIncluded;
      return [{
        expenseId,
        amount: commitmentAmounts[expenseId] ?? commitment.amount,
        isIncluded,
        updateFutureAmount: commitmentFutureUpdates[expenseId] ?? false,
        paidAnotherWay: !isIncluded && (commitmentPaidAnotherWay[expenseId] ?? commitment.paidAnotherWay),
        paymentMethodNote: isIncluded
          ? null
          : ((commitmentPaymentNotes[expenseId] ?? commitment.paymentMethodNote ?? '').trim() || null)
      }];
    });

    const invalidAmount = commitments.some(commitment => !commitment.amount || commitment.amount <= 0);
    if (invalidAmount) {
      setMessage('Todos os pagamentos precisam ter um valor positivo.');
      return;
    }

    const cards = (closing.cards ?? []).flatMap(card => {
      const cardId = card.creditCardId;
      if (!cardId) return [];

      const isIncluded = cardInclusions[cardId] ?? card.isIncluded;
      return [{
        creditCardId: cardId,
        amount: cardAmounts[cardId] ?? card.amount,
        isIncluded,
        isPaid: cardPaid[cardId] ?? card.isPaid,
        paymentMethodNote: isIncluded
          ? null
          : ((cardPaymentNotes[cardId] ?? card.paymentMethodNote ?? '').trim() || null)
      }];
    });

    const invalidCardAmount = cards.some(card => card.amount == null || card.amount < 0);
    if (invalidCardAmount) {
      setMessage('Os cartões precisam ter um valor zero ou positivo.');
      return;
    }

    setLoading(true);
    resetMessages();
    try {
      const saved = await saveMonthlyClosingDraft(closing.year, closing.month, commitments, closing.adjustments, cards);
      applyClosing(saved);
      setExpenses(previous => previous.map(expense => {
        const savedCommitment = commitments.find(commitment => commitment.expenseId === expense.id && commitment.updateFutureAmount);
        return savedCommitment ? { ...expense, amount: savedCommitment.amount } : expense;
      }));
      await refreshLatestForecast();
      setShowClosingModal(false);
      setMessage(closingMode === 'preview' ? 'Prévia salva.' : 'Rascunho do fechamento salvo.');
    } catch (err: any) {
      setMessage(err.message || 'Não foi possível salvar o rascunho do fechamento.');
    } finally {
      setLoading(false);
    }
  }

  async function handleFinalizeClosing() {
    if (!closing) return;
    if (hasUnsavedCommitmentChanges || adjustmentsDirty) {
      setMessage('Salve o rascunho antes de confirmar o fechamento.');
      return;
    }
    if (!confirm(`Confirmar o fechamento de ${String(closing.month).padStart(2, '0')}/${closing.year} com saldo residual de ${currency(closingResidualPreview)}?`)) return;
    setLoading(true);
    resetMessages();
    try {
      const finalized = await finalizeMonthlyClosing(closing.year, closing.month);
      applyClosing(finalized);
      setLatestClosing({
        year: finalized.year,
        month: finalized.month,
        residualBalance: finalized.residualBalance,
        forecastIncome: finalized.forecastIncome,
        forecastExpenses: finalized.forecastExpenses,
        forecastBalance: finalized.forecastBalance,
        finalizedAt: finalized.finalizedAt
      });
      await refreshLatestForecast();
      setMessage('Fechamento mensal finalizado.');
    } catch (err: any) {
      setMessage(err.message || 'Não foi possível finalizar o fechamento.');
    } finally {
      setLoading(false);
    }
  }

  async function handleReopenClosing() {
    if (!closing || !closing.isFinalized) return;
    if (!confirm(`Reabrir o fechamento de ${String(closing.month).padStart(2, '0')}/${closing.year}? O fechamento será desfeito e poderá ser realizado novamente.`)) return;

    setLoading(true);
    resetMessages();
    try {
      applyClosing(await reopenMonthlyClosing(closing.year, closing.month));
      await refreshLatestForecast();
      setMessage('Fechamento reaberto como rascunho.');
    } catch (err: any) {
      setMessage(err.message || 'Não foi possível reabrir o fechamento.');
    } finally {
      setLoading(false);
    }
  }

  async function handleDeleteEntry(entry: EntryDto) {
    if (!confirm(`Remover a entrada "${entry.name}"?`)) return;

    setLoading(true);
    resetMessages();
    try {
      await deleteEntry(entry.id);
      setEntryHistory(previous => previous.filter(item => item.id !== entry.id));
      if (entry.month === currentMonth && entry.year === currentYear) {
        setEntries(previous => previous.filter(item => item.id !== entry.id));
      }
      await refreshLatestForecast();
      setMessage('Entrada removida.');
    } catch (err: any) {
      setMessage(err.message || 'Não deu para remover a entrada.');
    } finally {
      setLoading(false);
    }
  }

  async function handleToggle(expense: ExpenseDto) {
    setLoading(true);
    resetMessages();
    try {
      const updated = await toggleExpense(expense.id);
      setExpenses(prev => prev.map(e => (e.id === updated.id ? updated : e)));
      await refreshLatestForecast();
    } catch {
      setMessage('Não consegui alternar agora.');
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Remover esta despesa?')) return;
    setLoading(true);
    resetMessages();
    try {
      await deleteExpense(id);
      setExpenses(prev => prev.filter(e => e.id !== id));
      await refreshLatestForecast();
    } catch {
      setMessage('Não consegui remover agora.');
    } finally {
      setLoading(false);
    }
  }

  function iniciarEdicao(exp: ExpenseDto) {
    setEditing({
      id: exp.id,
      name: exp.name,
      originalName: exp.originalName,
      amount: exp.amount,
      type: exp.type,
      installments: exp.type === ExpenseType.Installment ? (exp.installments ?? 1) : null,
      purchaseDate: exp.purchaseDate?.substring(0, 10) ?? null,
      dueDate: exp.dueDate?.substring(0, 10) ?? null,
      dueDay: exp.dueDay ?? null,
      autoPayment: exp.autoPayment ?? false,
      includeInForecast: exp.includeInForecast ?? true,
      isActive: exp.isActive,
      creditCardId: exp.creditCardId ?? null
    });
  }

  async function handleSalvarEdicao(e: React.FormEvent) {
    e.preventDefault();
    if (!editing) return;
    setLoading(true);
    resetMessages();
    try {
      const payload: EditExpenseModel = {
        ...editing,
        amount: Number(editing.amount),
        installments: editing.type === ExpenseType.Installment ? (editing.installments ?? 1) : null
      };
      const updated = await updateExpense(payload);
      setExpenses(prev => prev.map(x => (x.id === updated.id ? updated : x)));
      await refreshLatestForecast();
      setEditing(null);
      setMessage('Despesa atualizada.');
    } catch (err: any) {
      setMessage(err.message || 'Não deu para salvar as alterações.');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreditCardSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!newCreditCard.name.trim()) return;
    setLoading(true);
    resetMessages();
    try {
      const created = await addCreditCard(newCreditCard);
      setCreditCards(prev => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      setNewCreditCard({ name: '', brand: '', lastFourDigits: '', limit: '', closingDay: '', dueDay: '', isActive: true });
      setMessage('Cartão de crédito adicionado.');
    } catch (err: any) {
      setMessage(err.message || 'Não deu para salvar o cartão.');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreditCardUpdate(e: React.FormEvent) {
    e.preventDefault();
    if (!editingCreditCard) return;
    setLoading(true);
    resetMessages();
    try {
      const updated = await updateCreditCard(editingCreditCard.id, {
        name: editingCreditCard.name,
        brand: editingCreditCard.brand ?? '',
        lastFourDigits: editingCreditCard.lastFourDigits ?? '',
        limit: editingCreditCard.limit ?? '',
        closingDay: editingCreditCard.closingDay ?? '',
        dueDay: editingCreditCard.dueDay ?? '',
        isActive: editingCreditCard.isActive
      });
      setCreditCards(prev => prev.map(c => (c.id === updated.id ? updated : c)));
      setEditingCreditCard(null);
      setMessage('Cartão de crédito atualizado.');
    } catch (err: any) {
      setMessage(err.message || 'Não deu para salvar as alterações do cartão.');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreditCardToggle(card: CreditCardDto) {
    setLoading(true);
    resetMessages();
    try {
      const updated = await toggleCreditCard(card.id);
      setCreditCards(prev => prev.map(c => (c.id === updated.id ? updated : c)));
    } catch (err: any) {
      setMessage(err.message || 'Não consegui alternar o cartão agora.');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreditCardDelete(card: CreditCardDto) {
    if (!confirm(`Remover o cartão "${card.name}"?`)) return;
    setLoading(true);
    resetMessages();
    try {
      await deleteCreditCard(card.id);
      setCreditCards(prev => prev.filter(c => c.id !== card.id));
      if (editingCreditCard?.id === card.id) setEditingCreditCard(null);
      setMessage('Cartão de crédito removido.');
    } catch (err: any) {
      setMessage(err.message || 'Não consegui remover o cartão agora.');
    } finally {
      setLoading(false);
    }
  }

  function openCreditCards() {
    setEditingCreditCard(null);
    setShowCreditCardsModal(true);
  }

  function handleLogout() {
    clearSession();
    setAuthUser(null);
    setExpenses([]);
    setEntries([]);
    setEntryHistory([]);
    setClosing(null);
    setLatestClosing(null);
    setSavedForecast(null);
    setCreditCards([]);
    setShowCreditCardsModal(false);
    setEditingCreditCard(null);
    setMessage('Sessão encerrada.');
  }

  if (authLoading) {
    return (
      <div className="app-shell">
        <main className="main-content">
          <section className="hero-card gradient login-card">
            <div className="hero-title-row">
              <h1>DevoQuanto</h1>
              <p className="hero-month-greeting">Bem-vindo ao mês de {currentMonthName.charAt(0).toUpperCase() + currentMonthName.slice(1)}!</p>
            </div>
            <p className="lede">Carregando sessão...</p>
          </section>
        </main>
      </div>
    );
  }

  if (!authUser) {
    return (
      <div className="app-shell">
        <main className="main-content">
          <section className="hero-card gradient login-card">
            <div className="hero-title-row">
              <h1>DevoQuanto</h1>
              <p className="hero-month-greeting">Bem-vindo ao mês de {currentMonthName.charAt(0).toUpperCase() + currentMonthName.slice(1)}!</p>
            </div>
            <p className="lede">Faça login com Google para ver seus dados.</p>
            <div id="google-signin-btn" />
            {message && <div className="toast">{message}</div>}
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <main className="main-content">
        <section className="hero-card gradient">
          <div>
            <div className="session-strip">
              <div className="session-user">
                {authUser.pictureUrl && <img src={authUser.pictureUrl} alt={authUser.name} className="session-avatar" />}
                <span>{authUser.name}</span>
              </div>
              <button className="ghost" onClick={handleLogout}>Sair</button>
            </div>
            <div className="hero-title-row">
              <h1>DevoQuanto</h1>
              <p className="hero-month-greeting">Bem-vindo ao mês de {currentMonthName.charAt(0).toUpperCase() + currentMonthName.slice(1)}!</p>
            </div>
            <p className="lede">
              Controle suas despesas e entradas. Use os atalhos para lançar novos itens sem sair da visão geral.
            </p>
            <div className="menu-pills">
              <button className="nav-item menu-shortcut shortcut-installment" onClick={() => openCategory(ExpenseType.Installment)}>
                <span className="material-symbols-outlined" aria-hidden="true">receipt_long</span>
                <span>Parcelamento<span className="shortcut-mobile-break">/Financiamento</span></span>
              </button>
              <button className="nav-item menu-shortcut shortcut-card" onClick={() => openCategory(ExpenseType.RecurringCard)}>
                <span className="material-symbols-outlined" aria-hidden="true">credit_card</span>
                <span>Assinatura no cartão</span>
              </button>
              <div className="menu-pill-with-info">
                <button className="nav-item menu-shortcut shortcut-monthly" onClick={() => openCategory(ExpenseType.RecurringOffCard)}>
                  <span className="material-symbols-outlined" aria-hidden="true">event_repeat</span>
                  <span>Pagamentos manuais</span>
                </button>
                <span
                  className="info-tooltip"
                  tabIndex={0}
                  aria-label="Somente pagamentos manuais são apresentados durante o fechamento do mês."
                >
                  <span className="material-symbols-outlined" aria-hidden="true">info</span>
                  <span className="tooltip-content" role="tooltip">
                    Somente pagamentos manuais são apresentados durante o fechamento do mês.
                  </span>
                </span>
              </div>
              <button className="nav-item menu-shortcut shortcut-entry" onClick={() => setShowEntryModal(true)}>
                <span className="material-symbols-outlined" aria-hidden="true">payments</span>
                <span>Entrada</span>
              </button>
              <button className="nav-item menu-shortcut shortcut-preview" onClick={() => void handleOpenClosing()}>
                <span className="material-symbols-outlined" aria-hidden="true">calculate</span>
                <span>Calcular prévia</span>
              </button>
              <button className="nav-item menu-shortcut shortcut-close" onClick={() => void handleOpenClosing('closing')}>
                <span className="material-symbols-outlined" aria-hidden="true">event_available</span>
                <span>Fechar mês</span>
              </button>
              <button className="nav-item menu-shortcut shortcut-credit-cards" onClick={openCreditCards}>
                <span className="material-symbols-outlined" aria-hidden="true">wallet</span>
                <span>Cartões de Crédito{creditCards.length > 0 ? ` (${creditCards.filter(c => c.isActive).length})` : ''}</span>
              </button>
            </div>
          </div>
        </section>

        <section className="insights-grid">
          <button className="insight-card highlight-card entry-total-button" onClick={openEntryHistory}>
            <p className="muted">{hasCurrentMonthEntries ? 'Total entrada no mês' : 'Previsão de entrada'}</p>
            <h2>{entryCardValue === undefined ? '—' : currency(entryCardValue)}</h2>
            <span className="entry-card-hint">
              {hasCurrentMonthEntries
                ? `Ver entradas de ${String(currentMonth).padStart(2, '0')}/${currentYear}`
                : latestClosing
                  ? 'Baseada na última foto de fechamento'
                  : 'Disponível após o primeiro fechamento'}
            </span>
          </button>
          <div className="insight-card">
            <p className="muted">Seu saldo do último fechamento</p>
            <h2 className={latestClosing && latestClosing.residualBalance < 0 ? 'negative-value' : undefined}>
              {latestClosing ? currency(latestClosing.residualBalance) : '—'}
            </h2>
          </div>
          <div className="insight-card">
            <p className="muted forecast-heading">
              Valores já comprometidos este mês
              <span className="info-tooltip" tabIndex={0} aria-label="Origem dos valores já comprometidos">
                <span className="material-symbols-outlined" aria-hidden="true">info</span>
                <span className="tooltip-content" role="tooltip">
                  Este valor considera somente os cadastros ativos de Pagamentos manuais. Parcelamentos/Financiamentos e assinaturas no cartão não entram neste total.
                </span>
              </span>
            </p>
            <h2 className="outflow-value">{outflowCurrency(totalRecorrenciaForaCartao)}</h2>
          </div>
          <div className="insight-card highlight-card">
            <p className="muted forecast-heading">
              {savedForecast
                ? `Previsão de fechamento para ${forecastMonthLabel(savedForecast.year, savedForecast.month)}`
                : `Previsão de fechamento para ${forecastMonthLabel(currentYear, currentMonth)}`}
              <span className="info-tooltip" tabIndex={0} aria-label="Detalhes do cálculo da previsão">
                <span className="material-symbols-outlined" aria-hidden="true">info</span>
                <span className="tooltip-content" role="tooltip">
                  {savedForecast
                    ? <>
                        Saldo do último fechamento anterior ao mês {currency(savedForecast.previousResidual)}
                        {' + entradas do mês '}{currency(savedForecast.totalEntries)}
                        {' − pagamentos incluídos '}{currency(savedForecast.totalCommitments)}
                        {' − cartões incluídos '}{currency(savedForecast.totalCards)}
                        {' − dívidas e ajustes '}{currency(savedForecast.totalAdjustments)}
                        {' = '}{currency(savedForecast.balance)}.
                        {' Usa os valores salvos na prévia ou no fechamento deste mês. Pagamentos e faturas desmarcados não são descontados. Sem um fechamento anterior, o saldo inicial é zero.'}
                      </>
                    : 'Saldo do último fechamento + entradas do mês − pagamentos e cartões incluídos − dívidas e ajustes salvos no mês.'}
                </span>
              </span>
            </p>
            <h2>{savedForecast ? currency(savedForecast.balance) : '—'}</h2>
          </div>
        </section>

        {despesasVencendo.length > 0 && (
          <div className="alert-bar" onClick={() => setShowVencendo(true)}>
            <strong>{despesasVencendo.length} despesa(s)</strong> vencendo nos próximos dias. Clique para ver detalhes.
          </div>
        )}

        <section className="panels-grid">
          <div className="panel highlight chart-panel">
            <div className="panel-head">
              <div>
                <p className="eyebrow">Tipos de despesas</p>
                <h3>Valores gastos</h3>
              </div>
              <strong className="outflow-value">{outflowCurrency(totalGrafico)}</strong>
            </div>
            <div className="chart-canvas">
              <Bar data={chartData} options={chartOptions} />
            </div>
          </div>

          <div className="panel highlight parcelamentos-panel">
            <div className="panel-head installment-panel-head">
              <div>
                <p className="eyebrow">Parcelamentos/Financiamentos</p>
                <h3>Últimas parcelas do mês</h3>
                <p className="installment-description">Compromissos que terminam em {currentMonthName.charAt(0).toUpperCase() + currentMonthName.slice(1)}.</p>
              </div>
              <div className="installment-total">
                <span>Total a encerrar</span>
                <strong className="outflow-value">{outflowCurrency(totalUltimoMesParcelas)}</strong>
              </div>
            </div>
            {parcelasUltimoMes.length === 0 ? (
              <div className="installment-empty">
                <span className="material-symbols-outlined" aria-hidden="true">celebration</span>
                <div>
                  <strong>Nenhuma última parcela neste mês</strong>
                  <p>Não há parcelamentos ou financiamentos terminando agora.</p>
                </div>
              </div>
            ) : (
              <div className="list-cards">
                {parcelasUltimoMes.map(item => (
                  <div key={item.expense.id} className="mini-card">
                    <div className="title">{item.expense.name}</div>
                    <p className="muted">
                      Compra: {item.expense.purchaseDate ? format(parseDate(item.expense.purchaseDate)!, 'dd/MM/yyyy') : '—'} · {item.expense.installments}x
                    </p>
                    <strong className="outflow-value">{outflowCurrency(item.valor)}</strong>
                  </div>
                ))}
              </div>
            )}
          </div>

        </section>

        <section className="panel highlight vencendo-hoje-panel">
          <div className="panel-head installment-panel-head">
            <div>
              <p className="eyebrow">Hoje · {format(new Date(), 'dd/MM/yyyy')}</p>
              <h3>Vencendo hoje</h3>
              <p className="installment-description">Pagamentos manuais e faturas de cartão com vencimento no dia de hoje.</p>
            </div>
            <div className="installment-total">
              <span>Total vencendo</span>
              <strong className="outflow-value">{outflowCurrency(totalVencendoHoje)}</strong>
            </div>
          </div>
          {contasVencendoHoje.manuais.length === 0 && contasVencendoHoje.cartoes.length === 0 ? (
            <div className="installment-empty">
              <span className="material-symbols-outlined" aria-hidden="true">celebration</span>
              <div>
                <strong>Nenhuma conta vencendo hoje</strong>
                <p>Nada com vencimento para o dia de hoje.</p>
              </div>
            </div>
          ) : (
            <div className="list-cards">
              {contasVencendoHoje.manuais.map(e => (
                <div key={e.id} className="mini-card">
                  <div className="title">{e.name}</div>
                  <p className="muted">Pagamento manual · dia {e.dueDay ?? '—'}</p>
                  <strong className="outflow-value">{outflowCurrency(e.amount)}</strong>
                </div>
              ))}
              {contasVencendoHoje.cartoes.map(({ card, fatura }) => (
                <div key={card.id} className="mini-card">
                  <div className="title">{card.name}{card.lastFourDigits ? ` · final ${card.lastFourDigits}` : ''}</div>
                  <p className="muted">Fatura do cartão · dia {card.dueDay}</p>
                  <strong className="outflow-value">{outflowCurrency(fatura)}</strong>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="panel">
          <div className="panel-head">
            <div>
              <p className="eyebrow">Lista</p>
              <h3>Despesas cadastradas</h3>
            </div>
            <div className="filters-column">
              <div className="filters">
                <input className="input" placeholder="Pesquisar por nome" value={filterText} onChange={e => setFilterText(e.target.value)} />
                <select className="input" value={filterType} onChange={e => setFilterType(e.target.value)}>
                  <option value="">Todas</option>
                  <option value={ExpenseType.Installment}>Parcelamento/Financiamento</option>
                  <option value={ExpenseType.RecurringCard}>Assinatura no cartão</option>
                  <option value={ExpenseType.RecurringOffCard}>Pagamentos manuais</option>
                </select>
                <select className="input" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                  <option value="active">Ativas</option>
                  <option value="inactive">Inativas</option>
                  <option value="all">Todas</option>
                </select>
              </div>
              <div className="badge total-badge">Total despesas ativas: {totalDespesasAtivas}</div>
            </div>
          </div>

          {filteredExpenses.length === 0 ? (
            <p className="muted">Nenhuma despesa encontrada com os filtros atuais.</p>
          ) : (
            <div className="table expenses-table">
              <div className="table-row table-header">
                <div>Nome</div>
                <div className="centered-header">Tipo</div>
                <div className="centered-header">Status</div>
                <div className="centered-header">Valor</div>
                <div className="actions-col">Ações</div>
              </div>
              {filteredExpenses
                .slice()
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                .map(expense => (
                  <div className="table-row" key={expense.id}>
                    <div>
                      <div className="title">{expense.name}</div>
                      {expense.originalName && <div className="muted">Fatura: {expense.originalName}</div>}
                      {expense.type === ExpenseType.Installment && expense.purchaseDate && (
                        <div className="muted">Compra: {format(parseDate(expense.purchaseDate)!, 'dd/MM/yyyy')}</div>
                      )}
                      {expense.type === ExpenseType.RecurringOffCard && expense.dueDate && (
                        <div className="muted">Vence: {format(parseDate(expense.dueDate)!, 'dd/MM/yyyy')}</div>
                      )}
                      {expense.type === ExpenseType.RecurringOffCard && expense.dueDay && (
                        <div className="muted">Dia de vencimento: {expense.dueDay}</div>
                      )}
                      {expense.type === ExpenseType.RecurringOffCard && (
                        <div className="muted">Previsão: {expense.includeInForecast === false ? 'não incluir' : 'incluir'}</div>
                      )}
                      {(expense.type === ExpenseType.Installment || expense.type === ExpenseType.RecurringCard) && (
                        <div className="muted">
                          Cartão: {creditCardLabel(expense.creditCardId) ?? 'Fora do cartão'}
                        </div>
                      )}
                      <div className="muted">Data cadastramento: {format(parseDate(expense.createdAt)!, 'dd/MM/yyyy')}</div>
                      {expense.type === ExpenseType.Installment && expense.installments && (
                        (() => {
                          const baseDate =
                            parseDate(expense.purchaseDate ?? '') ??
                            parseDate(expense.createdAt);
                          if (!baseDate) return null;
                          const monthsElapsed =
                            differenceInCalendarMonths(startOfMonth(new Date()), startOfMonth(baseDate)) + 1;
                          const remaining = Math.max(expense.installments - monthsElapsed, 0);
                          return <div className="muted">Parcelas restantes: {remaining}</div>;
                        })()
                      )}
                    </div>
                    <div className="centered">
                      <span className={`pill ${typeClass(expense.type)}`}>
                        {typeLabel(expense.type)}
                        {expense.type === ExpenseType.Installment && expense.installments ? ` · ${expense.installments}x` : ''}
                      </span>
                    </div>
                    <div className="centered">
                      <span className={`pill ${expense.isActive ? 'pill-green' : 'pill-muted'}`}>
                        {expense.isActive ? 'Ativo' : 'Inativo'}
                      </span>
                    </div>
                    <div className="centered outflow-value">{outflowCurrency(expense.amount)}</div>
                    <div className="actions-col">
                      <button className="ghost" onClick={() => iniciarEdicao(expense)} title="Editar">
                        <span className="material-symbols-outlined">edit</span>
                      </button>
                      <button className="ghost danger" onClick={() => handleDelete(expense.id)} title="Remover">
                        <span className="material-symbols-outlined">delete</span>
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          )}
          <div className="expenses-table-total" aria-live="polite">
            <span>Total das despesas exibidas ({filteredExpenses.length})</span>
            <strong className="outflow-value">{outflowCurrency(totalFilteredExpenses)}</strong>
          </div>
        </section>

        {message && <div className="toast">{message}</div>}

        {editing && (
          <div className="panel" style={{ position: 'sticky', bottom: 16, zIndex: 2 }}>
            <div className="panel-head">
              <div>
                <p className="eyebrow">Editar despesa</p>
                <h3>{editing.name}</h3>
              </div>
              <button className="ghost" onClick={() => setEditing(null)}>Fechar</button>
            </div>
            <form className="form-grid" onSubmit={handleSalvarEdicao}>
              <label>
                <span>Nome</span>
                <input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} required />
              </label>
              <label>
                <span>Nome original na fatura</span>
                <input value={editing.originalName ?? ''} onChange={e => setEditing({ ...editing, originalName: e.target.value })} />
              </label>
              <label>
                <span>Valor</span>
                <input type="number" step="0.01" value={editing.amount} onChange={e => setEditing({ ...editing, amount: e.target.value === '' ? '' : Number(e.target.value) })} required />
              </label>
              <label>
                <span>Tipo</span>
                <select
                  value={editing.type}
                  onChange={e => setEditing({ ...editing, type: Number(e.target.value) as ExpenseType })}
                >
                  <option value={ExpenseType.Installment}>Parcelamento/Financiamento</option>
                  <option value={ExpenseType.RecurringCard}>Assinatura no cartão</option>
                  <option value={ExpenseType.RecurringOffCard}>Pagamentos manuais</option>
                </select>
              </label>
              {editing.type === ExpenseType.Installment && (
                <>
                  <label>
                    <span>Parcelas</span>
                    <input type="number" min={1} value={editing.installments ?? ''} onChange={e => setEditing({ ...editing, installments: e.target.value === '' ? null : Number(e.target.value) })} />
                  </label>
                  <label>
                    <span>Data da compra</span>
                    <input type="date" value={editing.purchaseDate ?? ''} onChange={e => setEditing({ ...editing, purchaseDate: e.target.value || null })} />
                  </label>
                  <label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={editing.autoPayment}
                        onChange={e => setEditing({ ...editing, autoPayment: e.target.checked })}
                      />{' '}
                      Pagamento automático
                    </div>
                  </label>
                </>
              )}
              {editing.type === ExpenseType.RecurringOffCard && (
                <>
                  <label>
                    <span>Dia de vencimento</span>
                    <input type="number" min={1} max={31} value={editing.dueDay ?? ''} onChange={e => setEditing({ ...editing, dueDay: e.target.value === '' ? null : Number(e.target.value) })} />
                  </label>
                  <label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={editing.autoPayment}
                        onChange={e => setEditing({ ...editing, autoPayment: e.target.checked })}
                      />{' '}
                      Pagamento automático
                    </div>
                  </label>
                  <label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={editing.includeInForecast}
                        onChange={e => setEditing({ ...editing, includeInForecast: e.target.checked })}
                      />{' '}
                      Incluir na previsão de fechamento
                    </div>
                  </label>
                </>
              )}
              {(editing.type === ExpenseType.Installment || editing.type === ExpenseType.RecurringCard) && (
                <label>
                  <span>Cartão de crédito</span>
                  <select
                    value={editing.creditCardId ?? ''}
                    onChange={e => setEditing({ ...editing, creditCardId: e.target.value || null })}
                  >
                    <option value="">Fora do cartão</option>
                    {creditCards.filter(c => c.isActive).map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name}{c.lastFourDigits ? ` · final ${c.lastFourDigits}` : ''}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label>
                <span>Status</span>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="checkbox" checked={editing.isActive} onChange={e => setEditing({ ...editing, isActive: e.target.checked })} /> Ativo
                </div>
              </label>
              <div className="form-actions equal-buttons">
                <button type="button" className="ghost" onClick={() => setEditing(null)}>Cancelar</button>
                <button type="submit" className="success" disabled={loading}>Salvar</button>
              </div>
            </form>
          </div>
        )}

        {showVencendo && (
          <>
            <div className="modal-backdrop" onClick={() => setShowVencendo(false)}></div>
            <div className="modal">
              <div className="panel-head">
                <div>
                  <p className="eyebrow">Alertas</p>
                  <h3>Despesas vencendo</h3>
                </div>
                <button className="ghost" onClick={() => setShowVencendo(false)}>Fechar</button>
              </div>
              {despesasVencendo.length === 0 ? (
                <p className="muted">Nenhuma despesa vence nos próximos 7 dias.</p>
              ) : (
                <div className="table">
                  <div className="table-row table-header">
                    <div>Nome</div>
                    <div>Vencimento</div>
                    <div>Valor</div>
                  </div>
                  {despesasVencendo.map(d => (
                    <div className="table-row" key={d.id}>
                      <div>{d.name}</div>
                      <div>{format(parseDate(d.dueDate!)!, 'dd/MM/yyyy')}</div>
                      <div className="outflow-value">{outflowCurrency(d.amount)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </main>

      {/* Modais (criação/edição das categorias acontece inline no modal da categoria) */}
      {showEntryModal && (
        <>
          <div className="modal-backdrop" onClick={() => setShowEntryModal(false)}></div>
          <div className="modal modal-wide">
            <div className="panel-head">
              <div>
                <p className="eyebrow">Nova entrada</p>
                <h3>Adicionar entrada</h3>
              </div>
              <button className="ghost" onClick={() => setShowEntryModal(false)}>Fechar</button>
            </div>
            <form className="form-grid" onSubmit={handleEntrySubmit}>
              <label>
                <span>Descrição</span>
                <input value={newEntry.name} onChange={e => setNewEntry({ ...newEntry, name: e.target.value })} required />
              </label>
              <label>
                <span>Valor</span>
                <input type="number" min="0.01" step="0.01" value={newEntry.amount} onChange={e => setNewEntry({ ...newEntry, amount: e.target.value === '' ? '' : Number(e.target.value) })} required />
              </label>
              <label>
                <span>Mês</span>
                <select value={newEntry.month} onChange={e => setNewEntry({ ...newEntry, month: Number(e.target.value) })} required>
                  {Array.from({ length: 12 }, (_, index) => index + 1).map(month => (
                    <option key={month} value={month}>{new Date(2000, month - 1).toLocaleString('pt-BR', { month: 'long' })}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>Ano</span>
                <input type="number" min="1" value={newEntry.year} onChange={e => setNewEntry({ ...newEntry, year: Number(e.target.value) })} required />
              </label>
              <label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="checkbox" checked={newEntry.isSalary} onChange={e => setNewEntry({ ...newEntry, isSalary: e.target.checked })} />{' '}
                  Usar como salário-base da previsão
                </div>
              </label>
              <div className="form-actions equal-buttons">
                <button type="button" className="ghost" onClick={() => setShowEntryModal(false)}>Cancelar</button>
                <button type="submit" className="success" disabled={loading}>Adicionar entrada</button>
              </div>
            </form>
          </div>
        </>
      )}

      {showEntryHistoryModal && (
        <>
          <div className="modal-backdrop" onClick={() => setShowEntryHistoryModal(false)}></div>
          <div className="modal modal-wide entry-history-modal">
            <div className="panel-head">
              <div>
                <p className="eyebrow">Histórico</p>
                <h3>Entradas do mês</h3>
              </div>
              <button className="ghost" onClick={() => setShowEntryHistoryModal(false)}>Fechar</button>
            </div>
            <form
              className="entry-history-filters"
              onSubmit={event => {
                event.preventDefault();
                void loadEntryHistory(historyYear, historyMonth);
              }}
            >
              <label>
                <span>Mês</span>
                <select
                  value={historyMonth}
                  onChange={e => setHistoryMonth(Number(e.target.value))}
                >
                  {Array.from({ length: 12 }, (_, index) => index + 1).map(month => (
                    <option key={month} value={month}>{new Date(2000, month - 1).toLocaleString('pt-BR', { month: 'long' })}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>Ano</span>
                <input
                  type="number"
                  min="1"
                  value={historyYear}
                  onChange={e => setHistoryYear(Number(e.target.value))}
                />
              </label>
              <button type="submit" className="primary" disabled={loading}>Filtrar</button>
              <div className="entry-history-total">
                <span>Total do período</span>
                <strong>{currency(entryHistory.reduce((sum, entry) => sum + entry.amount, 0))}</strong>
              </div>
            </form>
            {loading && entryHistory.length === 0 ? (
              <p className="muted">Carregando entradas...</p>
            ) : entryHistory.length === 0 ? (
              <p className="muted">Nenhuma entrada cadastrada neste mês.</p>
            ) : (
              <div className="entry-history-list">
                {entryHistory.map(entry => (
                  <div className="entry-history-item" key={entry.id}>
                    <div>
                      <div className="title">{entry.name}</div>
                      <div className="muted">Adicionada em {format(parseDate(entry.createdAt)!, "dd/MM/yyyy 'às' HH:mm")}</div>
                    </div>
                    <div className="entry-history-actions">
                      <strong>{currency(entry.amount)}</strong>
                      <button
                        type="button"
                        className="ghost danger"
                        onClick={() => void handleDeleteEntry(entry)}
                        disabled={loading}
                        title="Remover entrada"
                        aria-label={`Remover entrada ${entry.name}`}
                      >
                        <span className="material-symbols-outlined">delete</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {showCreditCardsModal && (
        <>
          <div className="modal-backdrop" onClick={() => setShowCreditCardsModal(false)}></div>
          <div className="modal modal-wide entry-history-modal">
            <div className="panel-head">
              <div>
                <p className="eyebrow">Cadastro</p>
                <h3>Cartões de Crédito</h3>
              </div>
              <button className="ghost" onClick={() => setShowCreditCardsModal(false)}>Fechar</button>
            </div>

            <form className="form-grid" onSubmit={handleCreditCardSubmit}>
              <label>
                <span>Nome do cartão *</span>
                <input
                  value={newCreditCard.name}
                  onChange={e => setNewCreditCard({ ...newCreditCard, name: e.target.value })}
                  placeholder="Ex.: Nubank, Inter, Itaú"
                  required
                />
              </label>
              <label>
                <span>Bandeira</span>
                <input
                  value={newCreditCard.brand ?? ''}
                  onChange={e => setNewCreditCard({ ...newCreditCard, brand: e.target.value })}
                  placeholder="Ex.: Visa, Master, Elo"
                />
              </label>
              <label>
                <span>Últimos 4 dígitos</span>
                <input
                  value={newCreditCard.lastFourDigits ?? ''}
                  onChange={e => setNewCreditCard({ ...newCreditCard, lastFourDigits: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                  placeholder="1234"
                  inputMode="numeric"
                  maxLength={4}
                />
              </label>
              <label>
                <span>Limite (R$)</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={newCreditCard.limit ?? ''}
                  onChange={e => setNewCreditCard({ ...newCreditCard, limit: e.target.value === '' ? '' : Number(e.target.value) })}
                />
              </label>
              <label>
                <span>Dia do fechamento</span>
                <input
                  type="number"
                  min={1}
                  max={31}
                  value={newCreditCard.closingDay ?? ''}
                  onChange={e => setNewCreditCard({ ...newCreditCard, closingDay: e.target.value === '' ? '' : Number(e.target.value) })}
                />
              </label>
              <label>
                <span>Dia do vencimento</span>
                <input
                  type="number"
                  min={1}
                  max={31}
                  value={newCreditCard.dueDay ?? ''}
                  onChange={e => setNewCreditCard({ ...newCreditCard, dueDay: e.target.value === '' ? '' : Number(e.target.value) })}
                />
              </label>
              <div className="form-actions equal-buttons">
                <button type="submit" className="success" disabled={loading}>Adicionar cartão</button>
              </div>
            </form>

            {creditCards.length === 0 ? (
              <p className="muted">Nenhum cartão cadastrado ainda.</p>
            ) : (
              <div className="entry-history-list">
                {creditCards.map(card => (
                  <div className="entry-history-item" key={card.id}>
                    <div>
                      <div className="title">
                        {card.name}
                        {card.brand ? ` · ${card.brand}` : ''}
                        {card.lastFourDigits ? ` · final ${card.lastFourDigits}` : ''}
                      </div>
                      <div className="muted">
                        {card.limit != null ? `Limite ${currency(card.limit)} · ` : ''}
                        {card.closingDay ? `Fecha dia ${card.closingDay} · ` : ''}
                        {card.dueDay ? `Vence dia ${card.dueDay}` : ''}
                        {!card.limit && !card.closingDay && !card.dueDay ? 'Sem detalhes adicionais' : ''}
                      </div>
                      <div>
                        <span className={`pill ${card.isActive ? 'pill-green' : 'pill-muted'}`}>
                          {card.isActive ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>
                    </div>
                    <div className="entry-history-actions">
                      <button
                        type="button"
                        className="ghost"
                        onClick={() => setEditingCreditCard(card)}
                        disabled={loading}
                        title="Editar cartão"
                        aria-label={`Editar cartão ${card.name}`}
                      >
                        <span className="material-symbols-outlined">edit</span>
                      </button>
                      <button
                        type="button"
                        className="ghost"
                        onClick={() => void handleCreditCardToggle(card)}
                        disabled={loading}
                        title={card.isActive ? 'Desativar' : 'Ativar'}
                        aria-label={`${card.isActive ? 'Desativar' : 'Ativar'} cartão ${card.name}`}
                      >
                        <span className="material-symbols-outlined">{card.isActive ? 'toggle_on' : 'toggle_off'}</span>
                      </button>
                      <button
                        type="button"
                        className="ghost danger"
                        onClick={() => void handleCreditCardDelete(card)}
                        disabled={loading}
                        title="Remover cartão"
                        aria-label={`Remover cartão ${card.name}`}
                      >
                        <span className="material-symbols-outlined">delete</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {editingCreditCard && (
              <form className="form-grid" onSubmit={handleCreditCardUpdate} style={{ marginTop: 16, borderTop: '1px solid #eee', paddingTop: 16 }}>
                <div className="panel-head">
                  <div>
                    <p className="eyebrow">Editar cartão</p>
                    <h3>{editingCreditCard.name}</h3>
                  </div>
                  <button type="button" className="ghost" onClick={() => setEditingCreditCard(null)}>Cancelar edição</button>
                </div>
                <label>
                  <span>Nome do cartão *</span>
                  <input
                    value={editingCreditCard.name}
                    onChange={e => setEditingCreditCard({ ...editingCreditCard, name: e.target.value })}
                    required
                  />
                </label>
                <label>
                  <span>Bandeira</span>
                  <input
                    value={editingCreditCard.brand ?? ''}
                    onChange={e => setEditingCreditCard({ ...editingCreditCard, brand: e.target.value })}
                  />
                </label>
                <label>
                  <span>Últimos 4 dígitos</span>
                  <input
                    value={editingCreditCard.lastFourDigits ?? ''}
                    onChange={e => setEditingCreditCard({ ...editingCreditCard, lastFourDigits: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                    inputMode="numeric"
                    maxLength={4}
                  />
                </label>
                <label>
                  <span>Limite (R$)</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editingCreditCard.limit ?? ''}
                    onChange={e => setEditingCreditCard({ ...editingCreditCard, limit: e.target.value === '' ? null : Number(e.target.value) })}
                  />
                </label>
                <label>
                  <span>Dia do fechamento</span>
                  <input
                    type="number"
                    min={1}
                    max={31}
                    value={editingCreditCard.closingDay ?? ''}
                    onChange={e => setEditingCreditCard({ ...editingCreditCard, closingDay: e.target.value === '' ? null : Number(e.target.value) })}
                  />
                </label>
                <label>
                  <span>Dia do vencimento</span>
                  <input
                    type="number"
                    min={1}
                    max={31}
                    value={editingCreditCard.dueDay ?? ''}
                    onChange={e => setEditingCreditCard({ ...editingCreditCard, dueDay: e.target.value === '' ? null : Number(e.target.value) })}
                  />
                </label>
                <label>
                  <span>Status</span>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={editingCreditCard.isActive}
                      onChange={e => setEditingCreditCard({ ...editingCreditCard, isActive: e.target.checked })}
                    /> Ativo
                  </div>
                </label>
                <div className="form-actions equal-buttons">
                  <button type="button" className="ghost" onClick={() => setEditingCreditCard(null)}>Cancelar</button>
                  <button type="submit" className="success" disabled={loading}>Salvar</button>
                </div>
              </form>
            )}
          </div>
        </>
      )}

      {categoryModal && (
        <>
          <div className="modal-backdrop" onClick={() => setCategoryModal(null)}></div>
          <div className="modal closing-modal category-modal">
            <div className="panel-head">
              <div className="category-title">
                <span className="material-symbols-outlined category-icon" aria-hidden="true">
                  {categoryConfig[categoryModal].icon}
                </span>
                <div>
                  <p className="eyebrow">{categoryConfig[categoryModal].eyebrow}</p>
                  <h3>{categoryConfig[categoryModal].title}</h3>
                </div>
              </div>
              <button className="ghost" onClick={() => setCategoryModal(null)}>Fechar</button>
            </div>

            <p className="muted category-description">{categoryConfig[categoryModal].description}</p>

            <div className="category-stats">
              <div>
                <span>Total exibido ({categoryExpenses.length})</span>
                <strong className="outflow-value">{outflowCurrency(categoryTotal)}</strong>
              </div>
              <div>
                <span>Ativas</span>
                <strong>{categoryCounts.active}</strong>
              </div>
              <div>
                <span>Inativas</span>
                <strong>{categoryCounts.inactive}</strong>
              </div>
              <button
                type="button"
                className="success"
                onClick={() => { setShowCatCreate(v => !v); setCatEditing(null); }}
                disabled={loading}
              >
                {showCatCreate ? 'Fechar formulário' : categoryConfig[categoryModal].addLabel}
              </button>
            </div>

            {showCatCreate && categoryModal === ExpenseType.Installment && (
              <form className="form-grid category-form" onSubmit={handleParcelaSubmit}>
                <label>
                  <span>Nome</span>
                  <input value={novaParcela.name} onChange={e => setNovaParcela({ ...novaParcela, name: e.target.value })} required />
                </label>
                <label>
                  <span>Nome original na fatura (opcional)</span>
                  <input value={novaParcela.originalName} onChange={e => setNovaParcela({ ...novaParcela, originalName: e.target.value })} />
                </label>
                <label>
                  <span>Valor total</span>
                  <input type="number" step="0.01" value={novaParcela.amount} onChange={e => setNovaParcela({ ...novaParcela, amount: e.target.value === '' ? '' : Number(e.target.value) })} required />
                </label>
                <label>
                  <span>Parcelas</span>
                  <input type="number" min={1} value={novaParcela.installments} onChange={e => setNovaParcela({ ...novaParcela, installments: e.target.value === '' ? '' : Number(e.target.value) })} required />
                </label>
                <label>
                  <span>Data da compra</span>
                  <input type="date" value={novaParcela.purchaseDate ?? ''} onChange={e => setNovaParcela({ ...novaParcela, purchaseDate: e.target.value || null })} />
                </label>
                <label>
                  <span>Cartão de crédito</span>
                  <select
                    value={novaParcela.creditCardId ?? ''}
                    onChange={e => setNovaParcela({ ...novaParcela, creditCardId: e.target.value || '' })}
                  >
                    <option value="">Fora do cartão</option>
                    {creditCards.filter(c => c.isActive).map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name}{c.lastFourDigits ? ` · final ${c.lastFourDigits}` : ''}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={!!novaParcela.autoPayment}
                      onChange={e => setNovaParcela({ ...novaParcela, autoPayment: e.target.checked })}
                    />{' '}
                    Pagamento automático
                  </div>
                </label>
                <div className="form-actions equal-buttons">
                  <button type="button" className="ghost" onClick={() => setShowCatCreate(false)}>Cancelar</button>
                  <button type="submit" className="success" disabled={loading}>Adicionar parcelamento/financiamento</button>
                </div>
              </form>
            )}

            {showCatCreate && categoryModal === ExpenseType.RecurringCard && (
              <form className="form-grid category-form" onSubmit={handleRecorrenciaCartaoSubmit}>
                <label>
                  <span>Nome</span>
                  <input value={novaRecorrenciaCartao.name} onChange={e => setNovaRecorrenciaCartao({ ...novaRecorrenciaCartao, name: e.target.value })} required />
                </label>
                <label>
                  <span>Nome original na fatura (opcional)</span>
                  <input value={novaRecorrenciaCartao.originalName} onChange={e => setNovaRecorrenciaCartao({ ...novaRecorrenciaCartao, originalName: e.target.value })} />
                </label>
                <label>
                  <span>Valor</span>
                  <input type="number" step="0.01" value={novaRecorrenciaCartao.amount} onChange={e => setNovaRecorrenciaCartao({ ...novaRecorrenciaCartao, amount: e.target.value === '' ? '' : Number(e.target.value) })} required />
                </label>
                <label>
                  <span>Cartão de crédito</span>
                  <select
                    value={novaRecorrenciaCartao.creditCardId ?? ''}
                    onChange={e => setNovaRecorrenciaCartao({ ...novaRecorrenciaCartao, creditCardId: e.target.value || '' })}
                  >
                    <option value="">Fora do cartão</option>
                    {creditCards.filter(c => c.isActive).map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name}{c.lastFourDigits ? ` · final ${c.lastFourDigits}` : ''}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="form-actions equal-buttons">
                  <button type="button" className="ghost" onClick={() => setShowCatCreate(false)}>Cancelar</button>
                  <button type="submit" className="success" disabled={loading}>Adicionar assinatura</button>
                </div>
              </form>
            )}

            {showCatCreate && categoryModal === ExpenseType.RecurringOffCard && (
              <form className="form-grid category-form" onSubmit={handleRecorrenciaForaSubmit}>
                <label>
                  <span>Nome</span>
                  <input value={novaRecorrenciaFora.name} onChange={e => setNovaRecorrenciaFora({ ...novaRecorrenciaFora, name: e.target.value })} required />
                </label>
                <label>
                  <span>Valor</span>
                  <input type="number" step="0.01" value={novaRecorrenciaFora.amount} onChange={e => setNovaRecorrenciaFora({ ...novaRecorrenciaFora, amount: e.target.value === '' ? '' : Number(e.target.value) })} required />
                </label>
                <label>
                  <span>Dia de vencimento</span>
                  <input type="number" min={1} max={31} value={novaRecorrenciaFora.dueDay} onChange={e => setNovaRecorrenciaFora({ ...novaRecorrenciaFora, dueDay: e.target.value === '' ? '' : Number(e.target.value) })} />
                </label>
                <label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={!!novaRecorrenciaFora.autoPayment}
                      onChange={e => setNovaRecorrenciaFora({ ...novaRecorrenciaFora, autoPayment: e.target.checked })}
                    />{' '}
                    Pagamento automático
                  </div>
                </label>
                <label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={novaRecorrenciaFora.includeInForecast ?? true}
                      onChange={e => setNovaRecorrenciaFora({ ...novaRecorrenciaFora, includeInForecast: e.target.checked })}
                    />{' '}
                    Incluir na previsão de fechamento
                  </div>
                </label>
                <div className="form-actions equal-buttons">
                  <button type="button" className="ghost" onClick={() => setShowCatCreate(false)}>Cancelar</button>
                  <button type="submit" className="success" disabled={loading}>Adicionar pagamento manual</button>
                </div>
              </form>
            )}

            <form
              className={`category-filters${categoryModal !== ExpenseType.RecurringOffCard ? ' has-card-filter' : ''}`}
              onSubmit={e => e.preventDefault()}
            >
              <label>
                <span>Pesquisar</span>
                <input
                  className="input"
                  placeholder="Pesquisar por nome"
                  value={catSearch}
                  onChange={e => setCatSearch(e.target.value)}
                />
              </label>
              <label>
                <span>Status</span>
                <select className="input" value={catStatus} onChange={e => setCatStatus(e.target.value)}>
                  <option value="all">Todas</option>
                  <option value="active">Ativas</option>
                  <option value="inactive">Inativas</option>
                </select>
              </label>
              {(categoryModal === ExpenseType.RecurringCard || categoryModal === ExpenseType.Installment) && (
                <label>
                  <span>Cartão</span>
                  <select className="input" value={catCard} onChange={e => setCatCard(e.target.value)}>
                    <option value="">Todos os cartões</option>
                    <option value="none">Fora do cartão ({categoryCardOptions.withoutCard})</option>
                    {categoryCardOptions.cards.map(c => (
                      <option key={c.id} value={c.id}>{c.label}</option>
                    ))}
                  </select>
                </label>
              )}
            </form>

            {categoryExpenses.length === 0 ? (
              <p className="muted">
                {categoryBase.length === 0
                  ? 'Nenhum item cadastrado nesta categoria ainda.'
                  : 'Nenhum item encontrado com os filtros atuais.'}
              </p>
            ) : (
              <div className="category-list">
                {categoryExpenses.map(expense => (
                  <div className="category-row" key={expense.id}>
                    <div className="category-row-main">
                      <div className="title">{expense.name}</div>
                      {expense.originalName && <div className="muted">Fatura: {expense.originalName}</div>}
                      {expense.type === ExpenseType.Installment && expense.purchaseDate && (
                        <div className="muted">Compra: {format(parseDate(expense.purchaseDate)!, 'dd/MM/yyyy')}</div>
                      )}
                      {expense.type === ExpenseType.Installment && expense.installments && (
                        <div className="muted">Parcelas: {expense.installments}x</div>
                      )}
                      {(expense.type === ExpenseType.Installment || expense.type === ExpenseType.RecurringCard) && (
                        <div className="muted">Cartão: {creditCardLabel(expense.creditCardId) ?? 'Fora do cartão'}</div>
                      )}
                      {expense.type === ExpenseType.RecurringOffCard && expense.dueDay && (
                        <div className="muted">Dia de vencimento: {expense.dueDay}</div>
                      )}
                      <div>
                        <span className={`pill ${expense.isActive ? 'pill-green' : 'pill-muted'}`}>
                          {expense.isActive ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>
                    </div>
                    <div className="category-row-side">
                      <strong className="outflow-value">{outflowCurrency(expense.amount)}</strong>
                      <div className="category-row-actions">
                        <button
                          type="button"
                          className="ghost"
                          onClick={() => handleCategoryEdit(expense)}
                          disabled={loading}
                          title="Editar"
                          aria-label={`Editar ${expense.name}`}
                        >
                          <span className="material-symbols-outlined">edit</span>
                        </button>
                        <button
                          type="button"
                          className="ghost"
                          onClick={() => void handleToggle(expense)}
                          disabled={loading}
                          title={expense.isActive ? 'Desativar' : 'Ativar'}
                          aria-label={`${expense.isActive ? 'Desativar' : 'Ativar'} ${expense.name}`}
                        >
                          <span className="material-symbols-outlined">{expense.isActive ? 'toggle_on' : 'toggle_off'}</span>
                        </button>
                        <button
                          type="button"
                          className="ghost danger"
                          onClick={() => void handleDelete(expense.id)}
                          disabled={loading}
                          title="Remover"
                          aria-label={`Remover ${expense.name}`}
                        >
                          <span className="material-symbols-outlined">delete</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {catEditing && (
              <form className="form-grid category-form category-edit-form" onSubmit={handleCatEditSubmit}>
                <div className="panel-head">
                  <div>
                    <p className="eyebrow">Editar</p>
                    <h3>{catEditing.name}</h3>
                  </div>
                  <button type="button" className="ghost" onClick={() => setCatEditing(null)}>Cancelar edição</button>
                </div>
                <label>
                  <span>Nome</span>
                  <input value={catEditing.name} onChange={e => setCatEditing({ ...catEditing, name: e.target.value })} required />
                </label>
                {categoryModal !== ExpenseType.RecurringOffCard && (
                  <label>
                    <span>Nome original na fatura</span>
                    <input value={catEditing.originalName ?? ''} onChange={e => setCatEditing({ ...catEditing, originalName: e.target.value })} />
                  </label>
                )}
                <label>
                  <span>Valor</span>
                  <input type="number" step="0.01" value={catEditing.amount} onChange={e => setCatEditing({ ...catEditing, amount: e.target.value === '' ? '' : Number(e.target.value) })} required />
                </label>
                {categoryModal === ExpenseType.Installment && (
                  <>
                    <label>
                      <span>Parcelas</span>
                      <input type="number" min={1} value={catEditing.installments ?? ''} onChange={e => setCatEditing({ ...catEditing, installments: e.target.value === '' ? null : Number(e.target.value) })} />
                    </label>
                    <label>
                      <span>Data da compra</span>
                      <input type="date" value={catEditing.purchaseDate ?? ''} onChange={e => setCatEditing({ ...catEditing, purchaseDate: e.target.value || null })} />
                    </label>
                    <label>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                          type="checkbox"
                          checked={catEditing.autoPayment}
                          onChange={e => setCatEditing({ ...catEditing, autoPayment: e.target.checked })}
                        />{' '}
                        Pagamento automático
                      </div>
                    </label>
                  </>
                )}
                {categoryModal !== ExpenseType.RecurringOffCard && (
                  <label>
                    <span>Cartão de crédito</span>
                    <select
                      value={catEditing.creditCardId ?? ''}
                      onChange={e => setCatEditing({ ...catEditing, creditCardId: e.target.value || null })}
                    >
                      <option value="">Fora do cartão</option>
                      {creditCards
                        .filter(c => c.isActive || c.id === (catEditing.creditCardId ?? ''))
                        .map(c => (
                          <option key={c.id} value={c.id}>
                            {c.name}{c.lastFourDigits ? ` · final ${c.lastFourDigits}` : ''}{c.isActive ? '' : ' (inativo)'}
                          </option>
                        ))}
                    </select>
                  </label>
                )}
                {categoryModal === ExpenseType.RecurringOffCard && (
                  <>
                    <label>
                      <span>Dia de vencimento</span>
                      <input type="number" min={1} max={31} value={catEditing.dueDay ?? ''} onChange={e => setCatEditing({ ...catEditing, dueDay: e.target.value === '' ? null : Number(e.target.value) })} />
                    </label>
                    <label>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                          type="checkbox"
                          checked={catEditing.autoPayment}
                          onChange={e => setCatEditing({ ...catEditing, autoPayment: e.target.checked })}
                        />{' '}
                        Pagamento automático
                      </div>
                    </label>
                    <label>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                          type="checkbox"
                          checked={catEditing.includeInForecast}
                          onChange={e => setCatEditing({ ...catEditing, includeInForecast: e.target.checked })}
                        />{' '}
                        Incluir na previsão de fechamento
                      </div>
                    </label>
                  </>
                )}
                <label>
                  <span>Status</span>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input type="checkbox" checked={catEditing.isActive} onChange={e => setCatEditing({ ...catEditing, isActive: e.target.checked })} /> Ativo
                  </div>
                </label>
                <div className="form-actions equal-buttons">
                  <button type="button" className="ghost" onClick={() => setCatEditing(null)}>Cancelar</button>
                  <button type="submit" className="success" disabled={loading}>Salvar</button>
                </div>
              </form>
            )}
          </div>
        </>
      )}

      {showClosingModal && closing && (
        <>
          <div className="modal-backdrop" onClick={() => setShowClosingModal(false)}></div>
          <div className="modal closing-modal">
            <div className="panel-head">
              <div>
                <p className="eyebrow">{closingMode === 'preview' ? 'Calcular prévia' : 'Fechamento mensal'}</p>
                <h3>{closing.isFinalized ? 'Fechamento confirmado' : 'Revise os valores antes de confirmar'}</h3>
              </div>
              <button className="ghost" onClick={() => setShowClosingModal(false)}>Fechar</button>
            </div>

            <form
              className="closing-period"
              onSubmit={event => {
                event.preventDefault();
                void handleChangeClosingPeriod(closingYear, closingMonth);
              }}
            >
              <label>
                <span>Mês</span>
                <select value={closingMonth} onChange={e => setClosingMonth(Number(e.target.value))}>
                  {Array.from({ length: 12 }, (_, index) => index + 1).map(month => (
                    <option key={month} value={month}>{new Date(2000, month - 1).toLocaleString('pt-BR', { month: 'long' })}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>Ano</span>
                <input type="number" min="1" value={closingYear} onChange={e => setClosingYear(Number(e.target.value))} />
              </label>
              <button type="submit" className="ghost" disabled={loading}>Carregar</button>
              <span className={`pill ${closing.isFinalized ? 'pill-green' : 'pill-amber'}`}>
                {closing.isFinalized ? 'Finalizado' : 'Rascunho'}
              </span>
            </form>

            <div className="closing-summary">
              <div><span>Residual anterior</span><strong>{currency(closing.previousResidual)}</strong></div>
              <div><span>Entradas do mês</span><strong>{currency(closing.totalEntries)}</strong></div>
              <div><span>Pagamentos incluídos</span><strong className="outflow-value">{outflowCurrency(closingSelectedCommitmentsTotal)}</strong></div>
              <div><span>Cartões incluídos</span><strong className="outflow-value">{outflowCurrency(closingSelectedCardsTotal)}</strong></div>
              <div><span>Dívidas e ajustes</span><strong className="outflow-value">{outflowCurrency(closing.totalAdjustments)}</strong></div>
            </div>

            <section className="closing-section">
              <div className="panel-head">
                <div>
                  <p className="eyebrow">Faturas do mês</p>
                  <h3>Cartões de crédito</h3>
                </div>
              </div>
              {(closing.cards ?? []).length === 0 ? (
                <p className="muted">Nenhum cartão de crédito ativo.</p>
              ) : (
                <div className="closing-list">
                  {(closing.cards ?? []).map(card => {
                    const cardId = card.creditCardId;
                    const isIncluded = cardId ? (cardInclusions[cardId] ?? card.isIncluded) : card.isIncluded;
                    const isPaid = cardId ? (cardPaid[cardId] ?? card.isPaid) : card.isPaid;
                    return (
                      <div className={`closing-commitment-row ${isIncluded ? '' : 'excluded'}`} key={cardId ?? card.name}>
                        <label className="commitment-toggle">
                          <input
                            type="checkbox"
                            checked={isIncluded}
                            disabled={closing.isFinalized || !cardId || loading}
                            onChange={e => cardId && setCardInclusions(previous => ({ ...previous, [cardId]: e.target.checked }))}
                          />
                          <span>{card.name}</span>
                        </label>
                        <div className="commitment-value-column">
                          <small className="muted">Previsto {currency(card.baseAmount)}</small>
                          {closing.isFinalized || !cardId ? (
                            <strong className="outflow-value">{outflowCurrency(card.amount)}</strong>
                          ) : (
                            <input
                              type="text"
                              inputMode="numeric"
                              aria-label={`Fatura de ${card.name}`}
                              value={currency(cardAmounts[cardId] ?? card.amount)}
                              onFocus={event => event.currentTarget.select()}
                              onChange={e => setCardAmounts(previous => ({ ...previous, [cardId]: parseCurrencyInput(e.target.value) }))}
                            />
                          )}
                          {!closing.isFinalized && cardId && (
                            <label className="commitment-future-toggle">
                              <input
                                type="checkbox"
                                checked={isPaid}
                                disabled={loading}
                                onChange={e => setCardPaid(previous => ({ ...previous, [cardId]: e.target.checked }))}
                              />
                              <span>Fatura paga</span>
                            </label>
                          )}
                        </div>
                        <span className={`pill ${isIncluded ? 'pill-green' : 'pill-muted'}`}>
                          {isIncluded ? 'Incluído' : 'Ignorado'}
                        </span>
                        {card.amountOverridden && (
                          <span className="pill pill-amber" title={`Previsto ${currency(card.baseAmount)}`}>
                            Ajustado
                          </span>
                        )}
                        {isPaid && (
                          <span className="pill pill-green">Pago</span>
                        )}
                        {!isIncluded && (
                          <div className="commitment-payment-note">
                            <div className="commitment-payment-note-heading">
                              <span>Como será paga esta fatura?</span>
                            </div>
                            {closing.isFinalized || !cardId ? (
                              <strong>{card.paymentMethodNote}</strong>
                            ) : (
                              <input
                                value={cardPaymentNotes[cardId] ?? ''}
                                onChange={e => setCardPaymentNotes(previous => ({ ...previous, [cardId]: e.target.value }))}
                                placeholder="Como será paga esta fatura? (opcional)"
                              />
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {!closing.isFinalized && <p className="muted">O valor previsto vem das despesas vinculadas ao cartão. Se a fatura veio diferente (gastos extras no mês), ajuste o valor aqui — ele vale só para este mês. O próximo mês nasce de novo com o previsto do cadastro. Marque “Fatura paga” como checklist do que já foi pago.</p>}
            </section>

            <section className="closing-section">
              <div className="panel-head">
                <div>
                  <p className="eyebrow">Pagamentos manuais</p>
                  <h3>Valores usados no fechamento</h3>
                </div>
              </div>
              {closing.commitments.length === 0 ? (
                <p className="muted">Nenhum pagamento manual ativo.</p>
              ) : (
                <div className="closing-list">
                  {closing.commitments.map(commitment => {
                    const expenseId = commitment.expenseId;
                    const isIncluded = expenseId ? (commitmentInclusions[expenseId] ?? commitment.isIncluded) : commitment.isIncluded;
                    return (
                      <div className={`closing-commitment-row ${isIncluded ? '' : 'excluded'}`} key={expenseId ?? commitment.name}>
                        <label className="commitment-toggle">
                          <input
                            type="checkbox"
                            checked={isIncluded}
                            disabled={closing.isFinalized || !expenseId || loading}
                            onChange={e => expenseId && setCommitmentInclusions(previous => ({ ...previous, [expenseId]: e.target.checked }))}
                          />
                          <span>{commitment.name}</span>
                        </label>
                        <div className="commitment-value-column">
                          {closing.isFinalized || !expenseId ? (
                            <strong className="outflow-value">{outflowCurrency(commitment.amount)}</strong>
                          ) : (
                            <input
                              type="text"
                              inputMode="numeric"
                              aria-label={`Valor de ${commitment.name}`}
                              value={currency(commitmentAmounts[expenseId] ?? commitment.amount)}
                              onFocus={event => event.currentTarget.select()}
                              onChange={e => setCommitmentAmounts(previous => ({ ...previous, [expenseId]: parseCurrencyInput(e.target.value) }))}
                            />
                          )}
                          {!closing.isFinalized && expenseId && (
                            <label className="commitment-future-toggle">
                              <input
                                type="checkbox"
                                checked={commitmentFutureUpdates[expenseId] ?? false}
                                disabled={loading}
                                onChange={e => setCommitmentFutureUpdates(previous => ({ ...previous, [expenseId]: e.target.checked }))}
                              />
                              <span>Usar este valor nos próximos meses</span>
                            </label>
                          )}
                        </div>
                        <span className={`pill ${isIncluded ? 'pill-green' : 'pill-muted'}`}>
                          {isIncluded ? 'Incluído' : 'Ignorado'}
                        </span>
                        {!isIncluded && (
                          <div className="commitment-payment-note">
                            <div className="commitment-payment-note-heading">
                              <span>Como será pago este valor?</span>
                              <label className="commitment-alternative-payment-toggle">
                                <input
                                  type="checkbox"
                                  checked={expenseId
                                    ? (commitmentPaidAnotherWay[expenseId] ?? commitment.paidAnotherWay)
                                    : commitment.paidAnotherWay}
                                  disabled={closing.isFinalized || !expenseId || loading}
                                  onChange={e => expenseId && setCommitmentPaidAnotherWay(previous => ({ ...previous, [expenseId]: e.target.checked }))}
                                />
                                <span>Pago de outra forma</span>
                              </label>
                            </div>
                            {closing.isFinalized || !expenseId ? (
                              <strong>{commitment.paymentMethodNote}</strong>
                            ) : (
                              <input
                                value={commitmentPaymentNotes[expenseId] ?? ''}
                                onChange={e => setCommitmentPaymentNotes(previous => ({ ...previous, [expenseId]: e.target.value }))}
                                placeholder="Como será realizado? (opcional)"
                              />
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {!closing.isFinalized && <p className="muted">{closingMode === 'preview' ? (closing.userSavedDraft ? 'Retomando de onde parou. Ajuste as marcas e salve.' : 'Mês novo: tudo começa desmarcado. Marque os itens pagos e salve.') : 'Desmarque o que não entra neste fechamento e salve pelo botão abaixo. Os cálculos acompanham a edição; valores e opções só são aplicados ao salvar. A opção “Pago de outra forma” é apenas informativa e não altera os cálculos.'}</p>}
            </section>

            <section className="closing-section">
              <div className="panel-head">
                <div>
                  <p className="eyebrow">Uso único</p>
                  <h3>Dívidas e ajustes financeiros</h3>
                </div>
              </div>
              {!closing.isFinalized && (
                <form className="adjustment-form" onSubmit={handleAddAdjustment}>
                  <input placeholder="Descrição do ajuste" value={newAdjustment.description} onChange={e => setNewAdjustment({ ...newAdjustment, description: e.target.value })} required />
                  <input type="number" min="0.01" step="0.01" placeholder="Valor" value={newAdjustment.amount} onChange={e => setNewAdjustment({ ...newAdjustment, amount: e.target.value === '' ? '' : Number(e.target.value) })} required />
                  <button type="submit" className="ghost" disabled={loading}>Adicionar</button>
                </form>
              )}
              {closing.adjustments.length === 0 ? (
                <p className="muted">Nenhuma dívida ou ajuste informado.</p>
              ) : (
                <div className="closing-list">
                  {closing.adjustments.map(adjustment => (
                    <div className="closing-row adjustment-row" key={adjustment.id}>
                      <span>{adjustment.description}</span>
                      <strong className="outflow-value">{outflowCurrency(adjustment.amount)}</strong>
                      {!closing.isFinalized && (
                        <button type="button" className="ghost danger adjustment-delete" disabled={loading} onClick={() => void handleDeleteAdjustment(adjustment.id)} title="Remover ajuste" aria-label={`Remover ${adjustment.description}`}>
                          <span className="material-symbols-outlined">delete</span>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>

            {closing.amountChanges.length > 0 && (
              <section className="closing-section">
                <div className="panel-head">
                  <div>
                    <p className="eyebrow">Histórico</p>
                    <h3>Valores atualizados neste fechamento</h3>
                  </div>
                </div>
                <div className="closing-list">
                  {closing.amountChanges.map(change => (
                    <div className="closing-row" key={change.id}>
                      <span>{change.expenseName}</span>
                      <span className="muted outflow-value">{outflowCurrency(change.previousAmount)} → {outflowCurrency(change.newAmount)}</span>
                      <small className="muted">{format(parseDate(change.changedAt)!, "dd/MM/yyyy 'às' HH:mm")}</small>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <div className="closing-result">
              <div>
                <span>Saldo residual após o fechamento</span>
                <strong className={closingResidualPreview >= 0 ? 'positive-value' : 'negative-value'}>{currency(closingResidualPreview)}</strong>
              </div>
              <div>
                <span className="forecast-heading">
                  Previsão de fechamento para {forecastMonthLabel(closing.year, closing.month)}
                  <span className="info-tooltip" tabIndex={0} aria-label="Detalhes do cálculo da previsão">
                    <span className="material-symbols-outlined" aria-hidden="true">info</span>
                    <span className="tooltip-content" role="tooltip">
                      Saldo do último fechamento anterior ao mês {currency(closing.previousResidual)}
                      {' + entradas do mês '}{currency(closing.totalEntries)}
                      {' − pagamentos incluídos '}{currency(closingSelectedCommitmentsTotal)}
                      {' − cartões incluídos '}{currency(closingSelectedCardsTotal)}
                      {' − dívidas e ajustes '}{currency(closing.totalAdjustments)}
                      {' = '}{currency(closingResidualPreview)}.
                      {' O cálculo acompanha a edição. O card principal só muda ao salvar.'}
                    </span>
                  </span>
                </span>
                <strong className={closingResidualPreview >= 0 ? 'positive-value' : 'negative-value'}>{currency(closingResidualPreview)}</strong>
                <small>Saldo anterior + entradas − pagamentos e cartões incluídos − dívidas e ajustes do mês</small>
              </div>
            </div>

            {!closing.isFinalized && (
              <div className="form-actions equal-buttons">
                <button type="button" className="ghost" disabled={loading} onClick={() => void handleSaveClosingDraft()}>{closingMode === 'preview' ? 'Salvar prévia' : 'Salvar rascunho'}</button>
                {closingMode === 'closing' && <button type="button" className="success" disabled={loading} onClick={() => void handleFinalizeClosing()}>Confirmar fechamento</button>}
              </div>
            )}
            {closing.isFinalized && closingMode === 'closing' && (
              <div className="form-actions">
                <button type="button" className="ghost danger" disabled={loading} onClick={() => void handleReopenClosing()}>Reabrir fechamento</button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
